// TODO:
// remove moment once e3dc core date picker change moment to date-fns

import React from 'react'
import { TFunction } from 'i18next'
import { Controller, useFormContext } from 'react-hook-form'
import { z } from 'zod'
import moment from '@e3dc-react/shell/Libs/moment'
import { isValid, isBefore } from 'date-fns'
import { DatePicker, TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Grid } from '@mui/material'

export const TARIFF_NAME_MAX_CHAR = 64

/** return type of zod.refine is too complex and redundant to be manually specified here, let TypeScript infer the type on its own */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types,@typescript-eslint/explicit-function-return-type
export const createAddTariffSchema = (t: TFunction) =>
  z
    .object({
      tariffName: z
        .string({ required_error: t('emobility.tariffs.nameRequired') })
        .min(1, t('emobility.tariffs.nameRequired'))
        .max(TARIFF_NAME_MAX_CHAR, t('emobility.tariffs.nameToLong')),
      tariffGridPrice: z
        .string({ required_error: t('emobility.tariffs.priceRequired') })
        .min(1, t('emobility.tariffs.priceRequired'))
        .refine(value => !Number.isNaN(Number(value)), {
          message: t('emobility.tariffs.mustBeNumber'),
        }),
      tariffSolarPrice: z
        .string()
        .optional()
        .refine(value => value === '' || !Number.isNaN(Number(value)), {
          message: t('emobility.tariffs.mustBeNumber'),
        }),
      tariffDischargePrice: z
        .string()
        .optional()
        .refine(value => value === '' || !Number.isNaN(Number(value)), {
          message: t('emobility.tariffs.mustBeNumber'),
        }),
      startDate: z
        .string()
        .nullable()
        .refine(value => value === null || isValid(new Date(value)), {
          message: t('emobility.tariffs.validDateFormat'),
        }),
      endDate: z
        .string()
        .nullable()
        .refine(value => value === null || isValid(new Date(value)), {
          message: t('emobility.tariffs.validDateFormat'),
        }),
    })
    .refine(
      data => {
        /** it makes sense to validate start and end date against each other only when both have an actual value */
        if (!data.startDate || !data.endDate) {
          return true
        }

        const start = new Date(data.startDate)
        const end = new Date(data.endDate)

        return isBefore(start, end)
      },
      {
        message: t('emobility.tariffs.validEndDate'),
        path: ['endDate'],
      }
    )

export type AddTariffFormData = z.infer<ReturnType<typeof createAddTariffSchema>>

interface Props {
  currencySymbol: string | undefined
}

/**
 * Component with form fields for add new tariff
 * It requires react-hook-form context from the parent for the input fields
 */

export const AddTariffForm: React.FC<Props> = ({ currencySymbol }) => {
  const { t } = useCustomTranslation()
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext<AddTariffFormData>()

  return (
    <Grid container spacing={5}>
      <Grid item xs={12} mt={1}>
        <Controller
          name="tariffName"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.tariffs.tariffName')}
              helperText={errors.tariffName?.message}
              required
              clearable={false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffGridPrice"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <TextField
              type="number"
              step={0.1}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.tariffs.grid-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffGridPrice?.message}
              required
              clearable={false}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffSolarPrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              type="number"
              step={0.1}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.tariffs.solar-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffSolarPrice?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <Controller
          name="tariffDischargePrice"
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <TextField
              type="number"
              step={0.1}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              label={t('emobility.tariffs.discharge-price-field', { currencySymbol, context: currencySymbol ? 'currency-symbol' : undefined })}
              helperText={errors.tariffDischargePrice?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name="startDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              value={moment(value)}
              onChange={m => {
                /**
                 * Why the onChange logic is more involved than it may seem:
                 *
                 * - The DatePicker operates on `moment` objects rather than raw string input.
                 *   While this is convenient in most scenarios, it also means that even incomplete
                 *   or malformed user input still produces a `moment` instance.
                 *
                 * - In such cases, `m.isValid()` is the only dependable way to determine whether
                 *   the value represents a valid date, since the original input string is not available.
                 *
                 * - Without this check, partially entered values (e.g. "11/11/111") could be
                 *   incorrectly accepted as valid or automatically cleared, causing inconsistent
                 *   form state and validation behavior.
                 *
                 * - To maintain predictable handling, we explicitly normalize outputs:
                 *   - `null` → input was cleared
                 *   - invalid date → '' (flagged as invalid in the form)
                 *   - valid date → ISO string (normalized to start/end of day)
                 *
                 * In short, this approach preserves invalid/partial input and ensures it is properly
                 * reflected in validation, despite the DatePicker not exposing raw text values.
                 *
                 * The same logic applies to "endDate".
                 */
                let nextValue: string | null = null

                if (m) {
                  nextValue = m.isValid() ? m.startOf('day').toISOString() : ''
                }

                onChange(nextValue)
                trigger('endDate')
              }}
              label={t('emobility.tariffs.startDate')}
              helperText={errors.startDate?.message}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name="endDate"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DatePicker
              value={moment(value)}
              label={t('emobility.tariffs.endDate')}
              helperText={errors.endDate?.message}
              onChange={m => {
                if (!m) {
                  return onChange(null)
                }

                if (!m.isValid()) {
                  return onChange('')
                }

                onChange(m.endOf('day').toISOString())
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  )
}
